import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { BaseModal, type BaseModalProps } from '@hungryroot/ramen';
import { RDP, type RDPProps } from '../RDP/RDP';
import { RDPLoading } from '../RDPLoading/RDPLoading';
import styles from './RDPModal.module.css';

type RDPModalProps = BaseModalProps &
  RDPProps & {
    isLoading?: boolean;
  };

const FIXED_HEADER_THRESHOLD = 60;

export function RDPModal(props: RDPModalProps) {
  const { onProductContentScroll } = props;
  const [isHeaderShown, setIsHeaderShown] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const container = containerRef.current as HTMLDivElement;
        // this is not debounced because we want to update the header visibility
        // immediately as a user scrolls
        if (container.scrollTop > FIXED_HEADER_THRESHOLD) {
          setIsHeaderShown(true);
        } else {
          setIsHeaderShown(false);
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      (container as HTMLDivElement).addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        (container as HTMLDivElement).removeEventListener(
          'scroll',
          handleScroll
        );
      }
    };
  }, []);

  useEffect(() => {
    const debouncedScroll = debounce((scrollPercentage: number) => {
      onProductContentScroll?.(scrollPercentage);
    }, 300);

    // so that we don't fire the `onProductContentScroll` event on every
    // scroll change, we're debouncing this. this implementation is currently
    // done to minimize the noise sent to salad shooter, but could be extended to
    // other consumers as well
    const handleScrollEnd = () => {
      if (containerRef.current) {
        const container = containerRef.current as HTMLDivElement;
        const scrollPercentage =
          container.scrollTop /
          (container.scrollHeight - container.clientHeight);
        debouncedScroll(scrollPercentage);
      }
    };

    const container = containerRef.current;
    if (container) {
      (container as HTMLDivElement).addEventListener(
        'scrollend',
        handleScrollEnd
      );
    }

    return () => {
      if (container) {
        (container as HTMLDivElement).removeEventListener(
          'scrollend',
          handleScrollEnd
        );
        debouncedScroll.cancel();
      }
    };
  }, [onProductContentScroll]);

  return (
    <BaseModal
      {...props}
      ref={containerRef}
      contentClassName={styles['modal-content']}
      overlayClassName={props.overlayClassName}
    >
      {props.isLoading ? (
        <RDPLoading />
      ) : (
        <RDP {...props} header={isHeaderShown ? props.header : null} />
      )}
    </BaseModal>
  );
}
